import { overlappingCalendarBlocks, type CalendarBlockOverlap } from '@/utils/calendarBlockOverlap';
import { isCompleteCivilDate } from '@/utils/appointmentCivilDate';
import {
  clinicAddDays,
  clinicDayKey,
  clinicMinutesFromMidnight,
  parseClinicDayKey,
} from '@/utils/calendarClinicTime';

export const SUGGESTED_BLOCK_REASONS = [
  'Frokost',
  'Møde',
  'Administration',
  'Ferie',
  'Sygdom',
] as const;

export type BlockEditScope = 'single' | 'future' | 'all';

export type StaffOverlapKind = 'none' | 'patient' | 'block';

/**
 * Patient appointments cannot overlap: the save is rejected.
 * A calendar block may be booked over after an explicit confirm.
 * Cancelling the confirm leaves the appointment where it was.
 */
export function staffOverlapDecision(
  kind: StaffOverlapKind,
  confirmed: boolean,
): 'save' | 'stay' | 'reject' {
  switch (kind) {
    case 'none':
      return 'save';
    case 'patient':
      return 'reject';
    case 'block':
      return confirmed ? 'save' : 'stay';
    default: {
      const _exhaustive: never = kind;
      return _exhaustive;
    }
  }
}

export function isCountedPatientAppointment(item: {
  is_blocker?: boolean | null;
  status?: string | null;
}): boolean {
  if (item.is_blocker) return false;
  if (item.status === 'cancelled') return false;
  return true;
}

export function countedPatientAppointments<
  T extends { is_blocker?: boolean | null; status?: string | null },
>(items: readonly T[]): T[] {
  return items.filter(isCountedPatientAppointment);
}

export function isCalendarBlock(item: { is_blocker?: boolean | null }): boolean {
  return Boolean(item.is_blocker);
}

/**
 * Copenhagen midnight through the next Copenhagen midnight.
 * The civil dates are compared, so the 25-hour day on 25 Oct 2026 still counts.
 */
/** Midnight Copenhagen through the next midnight, including a 23- or 25-hour DST day. */
export function allDayBounds(civil: string): { start: Date; end: Date } | null {
  const start = parseClinicDayKey(civil);
  if (!start) return null;
  return { start, end: clinicAddDays(start, 1) };
}

export function isAllDayRange(start: Date, end: Date): boolean {
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return false;
  if (clinicMinutesFromMidnight(start) > 0.5 || clinicMinutesFromMidnight(end) > 0.5) return false;
  return clinicDayKey(end) === clinicDayKey(clinicAddDays(start, 1));
}

export function isAllDayCalendarBlock(item: {
  is_blocker?: boolean | null;
  starts_at: string;
  ends_at: string;
}): boolean {
  if (!isCalendarBlock(item)) return false;
  return isAllDayRange(new Date(item.starts_at), new Date(item.ends_at));
}

export function calendarBlockTitle(item: {
  service_name?: string | null;
  notes?: string | null;
}): string {
  const name = item.service_name?.trim();
  if (name) return name;
  const note = item.notes?.trim();
  if (note) return note;
  return 'Blokering';
}

export interface DragBlockSource {
  id: string;
  clinician_id: string | null;
  is_blocker?: boolean | null;
  service_name: string | null;
  starts_at: string;
  ends_at: string;
}

export function dragBlockOverlapLabel(input: {
  appointments: readonly DragBlockSource[];
  clinicianId: string | null;
  start: Date;
  end: Date;
  excludeId?: string | null;
  formatClock: (date: Date) => string;
}): string {
  if (!input.clinicianId) return '';
  const blocks: CalendarBlockOverlap[] = input.appointments
    .filter((row) => row.is_blocker && row.clinician_id === input.clinicianId)
    .map((row) => ({
      id: row.id,
      name: calendarBlockTitle(row),
      startsAt: new Date(row.starts_at),
      endsAt: new Date(row.ends_at),
    }));
  const hits = overlappingCalendarBlocks(blocks, input.start, input.end, input.excludeId);
  return hits
    .map(
      (block) =>
        `${block.name} ${input.formatClock(block.startsAt)}–${input.formatClock(block.endsAt)}`,
    )
    .join(', ');
}

export interface BlockServiceChoice {
  id: string;
  name: string;
  is_blocker: boolean;
  duration_minutes: number;
}

export type ResolvedBlockReason =
  | { kind: 'existing'; id: string; name: string }
  | { kind: 'create'; name: string; durationMinutes: number }
  | { kind: 'missing' };

export function resolveBlockServiceChoice(input: {
  services: readonly BlockServiceChoice[];
  selectedId: string | null;
  customLabel: string;
  durationMinutes: number;
}): ResolvedBlockReason {
  const blockers = input.services.filter((service) => service.is_blocker);
  const custom = input.customLabel.trim();
  if (custom) {
    const match = blockers.find(
      (service) => service.name.trim().toLowerCase() === custom.toLowerCase(),
    );
    if (match) return { kind: 'existing', id: match.id, name: match.name };
    return { kind: 'create', name: custom, durationMinutes: input.durationMinutes };
  }
  const selected = blockers.find((service) => service.id === input.selectedId);
  if (selected) return { kind: 'existing', id: selected.id, name: selected.name };
  return { kind: 'missing' };
}

export function blockReasonChoices(
  services: readonly { id: string; name: string; is_blocker: boolean }[],
): { id: string | null; name: string }[] {
  const blockers = services.filter((service) => service.is_blocker);
  const seen = new Set(blockers.map((service) => service.name.trim().toLowerCase()));
  const choices: { id: string | null; name: string }[] = blockers.map((service) => ({
    id: service.id,
    name: service.name,
  }));
  for (const name of SUGGESTED_BLOCK_REASONS) {
    if (!seen.has(name.toLowerCase())) choices.push({ id: null, name });
  }
  return choices;
}

const MAX_BLOCK_DAYS = 62;

export function eachCivilDay(from: string, until: string | null): string[] {
  if (!isCompleteCivilDate(from)) return [];
  const end = until && isCompleteCivilDate(until) ? until : from;
  if (end < from) return [];
  const days: string[] = [];
  let cursor = civilToLocalDate(from);
  const lastKey = end;
  while (clinicDayKey(cursor) <= lastKey) {
    days.push(clinicDayKey(cursor));
    if (days.length > MAX_BLOCK_DAYS) return [];
    cursor = clinicAddDays(cursor, 1);
  }
  return days;
}

function civilToLocalDate(civil: string): Date {
  return parseClinicDayKey(civil) ?? new Date(Number.NaN);
}

export interface BlockCreateSlot {
  clinicianId: string;
  start: Date;
  end: Date;
}

export function blockCreateSlots(input: {
  clinicianIds: readonly string[];
  allDay: boolean;
  startCivil: string;
  untilCivil: string | null;
  start: Date;
  end: Date;
}): BlockCreateSlot[] {
  const clinicians = [...new Set(input.clinicianIds.filter(Boolean))];
  if (clinicians.length === 0) return [];
  if (!input.allDay) {
    if (!(input.end > input.start)) return [];
    return clinicians.map((clinicianId) => ({
      clinicianId,
      start: input.start,
      end: input.end,
    }));
  }
  const days = eachCivilDay(input.startCivil, input.untilCivil);
  const slots: BlockCreateSlot[] = [];
  for (const civil of days) {
    const bounds = allDayBounds(civil);
    if (!bounds) continue;
    for (const clinicianId of clinicians) {
      slots.push({ clinicianId, start: bounds.start, end: bounds.end });
    }
  }
  return slots;
}

/**
 * Same half-open overlap as public-get-available-slots.
 * A block that ends at the next Copenhagen midnight covers every slot that
 * starts that day, and none that start at that midnight.
 */
export function intervalBlocksSlot(
  slotStart: Date,
  slotEnd: Date,
  busyStart: Date,
  busyEnd: Date,
): boolean {
  return slotStart < busyEnd && slotEnd > busyStart;
}

/** Full time-column box for an all-day block. The grid has no separate all-day row. */
export function allDayColumnBox(input: {
  dayStartHour: number;
  dayEndHour: number;
  hourHeight: number;
}): { top: number; height: number } {
  const hours = Math.max(1, input.dayEndHour - input.dayStartHour);
  return { top: 0, height: hours * input.hourHeight };
}

export interface SeriesMember {
  id: string;
  startsAt: Date;
}

export function seriesEditTargets(
  members: readonly SeriesMember[],
  anchorId: string,
  scope: BlockEditScope,
): SeriesMember[] {
  const anchor = members.find((member) => member.id === anchorId);
  switch (scope) {
    case 'single':
      return anchor ? [anchor] : [];
    case 'all':
      return [...members];
    case 'future': {
      if (!anchor) return [];
      return members.filter((member) => member.startsAt.getTime() >= anchor.startsAt.getTime());
    }
    default: {
      const _exhaustive: never = scope;
      return _exhaustive;
    }
  }
}

export function shiftedRange(
  originalStart: Date,
  deltaMs: number,
  durationMs: number,
): { start: Date; end: Date } {
  const start = new Date(originalStart.getTime() + deltaMs);
  return { start, end: new Date(start.getTime() + durationMs) };
}

const DEFAULT_BLOCK_MINUTES = 30;

function sameLocalCivilDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * Bloker tid keeps the end the user typed.
 * A reason such as Frokost has its own service duration (often 30 minutes)
 * and must not replace 09:00–10:00 with that duration.
 * On the same day, an end that is still after the new start stays put,
 * including when the old start was later than that end.
 * Moving the start onto another day, or past the end, keeps the length.
 */
export function blockEndAfterStartChange(input: {
  previousStart: Date | null;
  previousEnd: Date | null;
  nextStart: Date;
}): Date {
  const { previousStart, previousEnd, nextStart } = input;
  const fallback = new Date(nextStart.getTime() + DEFAULT_BLOCK_MINUTES * 60_000);
  if (
    previousEnd &&
    sameLocalCivilDay(previousEnd, nextStart) &&
    previousEnd.getTime() > nextStart.getTime()
  ) {
    return previousEnd;
  }
  if (previousStart && previousEnd && previousEnd.getTime() > previousStart.getTime()) {
    return new Date(nextStart.getTime() + (previousEnd.getTime() - previousStart.getTime()));
  }
  return fallback;
}

export type CalendarSlideDescription =
  | 'block_create'
  | 'block_edit'
  | 'appointment_create'
  | 'appointment_edit';

/**
 * Bloker tid has its own subtitle, including while editing.
 * A normal appointment keeps the confirmation and edit lines.
 */
export function calendarSlideDescription(input: {
  mode: 'create' | 'edit';
  block: boolean;
}): CalendarSlideDescription {
  if (input.block && input.mode === 'create') return 'block_create';
  if (input.block) return 'block_edit';
  if (input.mode === 'create') return 'appointment_create';
  return 'appointment_edit';
}

export type LockedAppointmentOpen = 'edit' | 'invoice' | 'missing_patient';

/**
 * A past patient appointment opens invoicing. A calendar block never does,
 * even when the start is already in the past in the browser's timezone.
 */
export function lockedAppointmentOpen(input: {
  isBlock: boolean;
  hasPatient: boolean;
  pastLocked: boolean;
}): LockedAppointmentOpen {
  if (input.isBlock) return 'edit';
  if (!input.pastLocked) return 'edit';
  if (!input.hasPatient) return 'missing_patient';
  return 'invoice';
}

/** Bloker tid can repeat before a reason service id exists. Appointments still need a type. */
export function recurrenceFieldDisabled(input: {
  pending: boolean;
  block: boolean;
  hasService: boolean;
}): boolean {
  if (input.pending) return true;
  if (input.block) return false;
  return !input.hasService;
}

/** A block has no patient invoice. Ordinary appointments with a patient do. */
export function appointmentRowIsInvoiceable(input: {
  patientId: string | null;
  isBlocker: boolean;
}): boolean {
  return Boolean(input.patientId) && !input.isBlocker;
}

export type BlockRemovalPlan =
  | { action: 'cancel_one'; toast: 'removed' }
  | { action: 'cancel_series'; toast: 'series_removed'; futureOnly: boolean };

/**
 * Removing a block uses the same cancel as CLISUP-164: one row through
 * cancel_appointment, or the series through delete_appointment_series
 * (which cancels each upcoming row). Status stays `cancelled`. A block
 * has no patient, so that audit row is not part of a patient history.
 */
export function blockRemovalPlan(input: {
  series: boolean;
  scope: 'single' | 'future' | 'all';
}): BlockRemovalPlan {
  if (!input.series || input.scope === 'single') {
    return { action: 'cancel_one', toast: 'removed' };
  }
  switch (input.scope) {
    case 'future':
      return { action: 'cancel_series', toast: 'series_removed', futureOnly: true };
    case 'all':
      return { action: 'cancel_series', toast: 'series_removed', futureOnly: false };
    default: {
      const _exhaustive: never = input.scope;
      return _exhaustive;
    }
  }
}

/** Normal appointments still follow the chosen service length. */
export function appointmentEndForService(start: Date, durationMinutes: number): Date {
  const minutes =
    Number.isFinite(durationMinutes) && durationMinutes > 0
      ? durationMinutes
      : DEFAULT_BLOCK_MINUTES;
  return new Date(start.getTime() + minutes * 60_000);
}
